import { getPdfWorkerSrc } from "config/env";
import * as pdfjsLib from "pdfjs-dist";
import type { TextItem } from "pdfjs-dist/types/src/display/api";

pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc(pdfjsLib.version);

const MIN_TEXT_LENGTH = 20;
const MAX_TEXT_PAGES = 7;
const OCR_CANVAS_SCALE = 2;
const MIN_OCR_CONFIDENCE = 50;

const isTextItem = (item: unknown): item is TextItem => typeof item === "object" && item !== null && "str" in item;

/**
 * Extracts embedded text from up to {@link pageCount} pages, returning early once enough text is found.
 * @param pdf - The parsed PDF document.
 * @param pageCount - Maximum number of pages to extract text from.
 * @returns The concatenated text content.
 */
const extractTextFromPages = async (pdf: pdfjsLib.PDFDocumentProxy, pageCount: number): Promise<string> => {
  let fullText = "";

  for (let i = 1; i <= pageCount; i += 1) {
    // Pages must be processed sequentially to allow early return once enough text is found
    // eslint-disable-next-line no-await-in-loop
    const page = await pdf.getPage(i);
    // eslint-disable-next-line no-await-in-loop
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .filter(isTextItem)
      .map((item) => item.str)
      .join(" ");
    fullText += pageText;

    if (fullText.trim().length >= MIN_TEXT_LENGTH) {
      return fullText.trim();
    }
  }

  return fullText.trim();
};

/**
 * Rotates a canvas 90° clockwise by creating a new canvas with swapped dimensions.
 * @param source - The canvas to rotate.
 * @returns A new canvas with the rotated image.
 */
const rotateCanvas = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const rotated = document.createElement("canvas");
  rotated.width = source.height;
  rotated.height = source.width;

  const ctx = rotated.getContext("2d");

  if (ctx) {
    ctx.translate(rotated.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(source, 0, 0);
  }

  return rotated;
};

/**
 * Extracts text from a scanned PDF by rendering the first page to a canvas and running Tesseract OCR.
 * If the initial recognition confidence is too low (e.g. rotated content), retries with 90°, 180°, and
 * 270° rotations and returns the result with the highest confidence.
 * @param pdf - The parsed PDF document.
 * @returns The best OCR text result across all attempted orientations.
 */
const extractTextViaOcr = async (pdf: pdfjsLib.PDFDocumentProxy): Promise<string> => {
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: OCR_CANVAS_SCALE });

  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  const context = canvas.getContext("2d");

  if (!context) {
    return "";
  }

  await page.render({ canvas, canvasContext: context, viewport }).promise;

  const { createWorker } = await import("tesseract.js");

  const worker = await createWorker("deu");
  const {
    data: { text, confidence },
  } = await worker.recognize(canvas);

  let bestText = text;
  let bestConfidence = confidence;

  if (bestConfidence < MIN_OCR_CONFIDENCE || bestText.trim().length < MIN_TEXT_LENGTH) {
    let currentCanvas = canvas;

    // Try 90°, 180°, 270° rotations sequentially, stop early if confidence is good enough
    for (let i = 0; i < 3; i += 1) {
      const rotatedCanvas = rotateCanvas(currentCanvas);
      currentCanvas = rotatedCanvas;

      // Each rotation attempt must be sequential since it depends on the previous canvas
      const {
        data: { text: rotatedText, confidence: rotatedConfidence },
        // eslint-disable-next-line no-await-in-loop -- we do not want to run this in parallel
      } = await worker.recognize(rotatedCanvas);

      if (rotatedConfidence > bestConfidence) {
        bestText = rotatedText;
        bestConfidence = rotatedConfidence;
      }

      if (bestConfidence >= MIN_OCR_CONFIDENCE) {
        break;
      }
    }

    if (currentCanvas !== canvas) {
      currentCanvas.remove();
    }
  }

  await worker.terminate();
  canvas.remove();

  return bestText.trim();
};

/**
 * Extracts text from a PDF file, using embedded text layers when available or falling back to OCR.
 * @param file - The PDF file to extract text from.
 * @returns The extracted text content.
 */
export const extractPdfText = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageCount = Math.min(pdf.numPages, MAX_TEXT_PAGES);

  const firstPage = await pdf.getPage(1);
  const firstPageContent = await firstPage.getTextContent();
  const hasTextItems = firstPageContent.items.some((item) => isTextItem(item) && item.str.trim().length > 0);

  if (hasTextItems) {
    return extractTextFromPages(pdf, pageCount);
  }

  return extractTextViaOcr(pdf);
};
